import React, { useState } from 'react';

/**
 * MobileDatePickerModal
 * 
 * An executive bottom-sheet/modal calendar tailored specifically for mobile viewports.
 * Displays clean month navigation, weekday headers, interactive day grid with past-date disabling,
 * and quick-select buttons ("Today", "Tomorrow").
 */
const MobileDatePickerModal = ({ isOpen, onClose, selectedDate, onSelectDate, minDate }) => {
  // Initial reference date
  const parseInitialDate = () => {
    if (selectedDate) {
      const parts = selectedDate.split('-');
      if (parts.length === 3) {
        return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
      }
    }
    return new Date();
  };

  const initial = parseInitialDate();
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth()); // 0-indexed

  if (!isOpen) return null;

  // Today reference at midnight
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const minAllowedDate = minDate ? new Date(minDate + 'T00:00:00') : today;

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  // Format date helper: YYYY-MM-DD
  const formatDateString = (year, month, day) => {
    const mm = String(month + 1).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${year}-${mm}-${dd}`;
  };

  const handleDateClick = (day) => {
    const formatted = formatDateString(viewYear, viewMonth, day);
    onSelectDate(formatted);
    onClose();
  };

  const handleQuickPick = (offsetDays) => {
    const target = new Date();
    target.setDate(target.getDate() + offsetDays);
    const formatted = formatDateString(target.getFullYear(), target.getMonth(), target.getDate());
    onSelectDate(formatted);
    onClose();
  };

  // Calendar calculations
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  // Selected date parts for highlighting
  let selY = -1, selM = -1, selD = -1;
  if (selectedDate) {
    const parts = selectedDate.split('-');
    if (parts.length === 3) {
      selY = parseInt(parts[0], 10);
      selM = parseInt(parts[1], 10) - 1;
      selD = parseInt(parts[2], 10);
    }
  }

  // Prevent navigating to months completely in the past
  const isPrevMonthDisabled = 
    viewYear < today.getFullYear() || 
    (viewYear === today.getFullYear() && viewMonth <= today.getMonth());

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:hidden">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-slate-950/80 backdrop-blur-md transition-opacity animate-fadeIn"
        onClick={onClose}
      />

      {/* Bottom Sheet Container */}
      <div className="relative w-full bg-[#0d1630] border-t border-slate-700/80 rounded-t-3xl p-5 shadow-2xl z-10 max-h-[90vh] overflow-y-auto animate-slideUp">
        {/* Drag handle pill */}
        <div className="flex justify-center mb-3">
          <div className="w-12 h-1.5 bg-slate-700 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-slate-800">
          <div>
            <span className="text-[11px] font-bold uppercase tracking-wider text-blue-400">Select Date</span>
            <h3 className="text-lg font-extrabold text-white">Pickup Date</h3>
          </div>
          <button 
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-800/80 border border-slate-700 text-slate-300 hover:text-white flex items-center justify-center transition-colors"
          >
            <span className="material-symbols-outlined !text-lg">close</span>
          </button>
        </div>

        {/* Quick select shortcuts */}
        <div className="flex gap-2.5 my-3.5">
          <button
            type="button"
            onClick={() => handleQuickPick(0)}
            className="flex-1 py-2 px-3 rounded-xl bg-slate-900 border border-slate-700 text-xs font-semibold text-slate-200 hover:border-blue-500 hover:text-white transition-all flex items-center justify-center gap-1.5"
          >
            <span className="material-symbols-outlined !text-sm text-blue-400">today</span>
            Today
          </button>
          <button
            type="button"
            onClick={() => handleQuickPick(1)}
            className="flex-1 py-2 px-3 rounded-xl bg-slate-900 border border-slate-700 text-xs font-semibold text-slate-200 hover:border-blue-500 hover:text-white transition-all flex items-center justify-center gap-1.5"
          >
            <span className="material-symbols-outlined !text-sm text-sky-400">event</span>
            Tomorrow
          </button>
        </div>

        {/* Month Navigation */}
        <div className="flex items-center justify-between py-2 px-1 mb-2">
          <span className="text-base font-extrabold text-white font-sans">
            {monthNames[viewMonth]} {viewYear}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={handlePrevMonth}
              disabled={isPrevMonthDisabled}
              className={`w-8 h-8 rounded-lg border flex items-center justify-center transition-all ${
                isPrevMonthDisabled 
                  ? 'border-slate-800 text-slate-600 cursor-not-allowed' 
                  : 'border-slate-700 bg-slate-900 text-slate-200 hover:border-blue-400'
              }`}
            >
              <span className="material-symbols-outlined !text-base">chevron_left</span>
            </button>
            <button
              type="button"
              onClick={handleNextMonth}
              className="w-8 h-8 rounded-lg border border-slate-700 bg-slate-900 text-slate-200 hover:border-blue-400 flex items-center justify-center transition-all"
            >
              <span className="material-symbols-outlined !text-base">chevron_right</span>
            </button>
          </div>
        </div>

        {/* Weekday Labels */}
        <div className="grid grid-cols-7 gap-1 text-center mb-1.5">
          {weekDays.map((wd) => (
            <div key={wd} className="text-[11px] font-bold text-slate-400 py-1 uppercase tracking-wider">
              {wd}
            </div>
          ))}
        </div>

        {/* Days Grid */}
        <div className="grid grid-cols-7 gap-1 text-center">
          {/* Empty cells before month starts */}
          {Array.from({ length: firstDayOfWeek }).map((_, i) => (
            <div key={`empty-${i}`} className="h-10" />
          ))}

          {/* Month Day Cells */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const currentCellDate = new Date(viewYear, viewMonth, day);
            currentCellDate.setHours(0, 0, 0, 0);

            const isPast = currentCellDate < minAllowedDate;
            const isSelected = selY === viewYear && selM === viewMonth && selD === day;
            const isToday = 
              today.getFullYear() === viewYear && 
              today.getMonth() === viewMonth && 
              today.getDate() === day;

            let btnClass = "w-full h-10 rounded-xl text-xs font-semibold flex items-center justify-center transition-all relative font-mono ";

            if (isSelected) {
              btnClass += "bg-blue-600 text-white font-bold shadow-lg shadow-blue-600/40 ring-2 ring-blue-400/50";
            } else if (isPast) {
              btnClass += "text-slate-600 cursor-not-allowed opacity-40";
            } else {
              btnClass += "text-slate-200 bg-slate-900/50 hover:bg-slate-800 hover:text-white border border-slate-800/80 active:scale-95";
            }

            return (
              <button
                key={day}
                type="button"
                disabled={isPast}
                onClick={() => handleDateClick(day)}
                className={btnClass}
              >
                {day}
                {isToday && !isSelected && (
                  <span className="absolute bottom-1 w-1 h-1 rounded-full bg-blue-400" />
                )}
              </button>
            );
          })}
        </div>

        {/* Done / Close Button */}
        <div className="mt-5 pt-3 border-t border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold uppercase tracking-wider transition-colors border border-slate-700"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileDatePickerModal;
